import { registerPageInit } from '../barba/page-init-registry';
import '../../css/_root.scss';
import '../../css/pages/residency/residency.scss';
import '../../css/pages/residency/residency-become.scss';
import '../../css/templates/header-promo.scss';
import { beforeSubmit } from '@/js/utils/beforeSubmit';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { submitFormWithCaptcha } from '@/js/utils/submitFormWithCaptcha';
import { getRecaptchaErrorMessage } from '@/js/utils/getRecaptchaToken';
import { showToast } from '@/js/utils/show-toast';
import { trackYaGoal } from '@/js/utils/yandex-metrika';
import notificationsWordings from '@/wordings/notificationWordings.json';
import {
  getResidencyBecomeDocumentsUpload,
  initResidencyBecomeDocumentsUpload,
} from '../widgets/residency-become-documents-upload';

const RESIDENCE_BECOME_ENDPOINT = getApiUrl('/api/form/become-resident/');
const FORM_SELECTOR = '[id^="residentBecomeForm"]';

class ResidencyPage {
  private select: HTMLSelectElement | null = null;
  private otherBusinessFieldWrapper: Element | null = null;
  private otherInput: HTMLInputElement | null = null;
  private form: HTMLFormElement | null = null;
  private submitButton: HTMLButtonElement | null = null;
  private submitButtonText = '';
  private isSubmitting = false;
  private privacyOptionId = '';

  constructor() {
    const { residencyBecomeWordings } = notificationsWordings;
    this.successMessageHtml = `<p class='toast-template__title'>${residencyBecomeWordings.title}</p><p class='toast-template__description'>${residencyBecomeWordings.description}</p>`;
    this.errorMessageHtml = `<p class='toast-template__title'>${residencyBecomeWordings.errorTitle}</p><p class='toast-template__description'>${residencyBecomeWordings.errorDescription}</p>`;
  }

  private successMessageHtml: string;
  private errorMessageHtml: string;

  public init(root?: ParentNode): void {
    initResidencyBecomeDocumentsUpload(root);

    this.form = (root ?? document).querySelector<HTMLFormElement>(FORM_SELECTOR);
    this.select = document.getElementById('business_area') as HTMLSelectElement;
    this.otherBusinessFieldWrapper = document.querySelector('.other-reason-field-wrapper');
    this.otherInput = document.getElementById('other_business') as HTMLInputElement;
    this.submitButton = this.form?.querySelector<HTMLButtonElement>('button[type="submit"]') ?? null;

    if (this.submitButton) {
      this.submitButtonText = this.submitButton.innerHTML;
    }

    void this.fetchFormMeta();
    this.bindEvents();
  }

  private async fetchFormMeta(): Promise<void> {
    try {
      const response = await fetch(RESIDENCE_BECOME_ENDPOINT, {
        method: 'GET',
        credentials: 'same-origin',
      });
      const result = (await response.json()) as {
        status?: string;
        msg?: { privacy?: Array<{ id: string }> };
      };

      if (response.ok && result.status === 'success') {
        this.privacyOptionId = result.msg?.privacy?.[0]?.id ?? '';
      }
    } catch {
      // Форма остаётся рабочей даже без метаданных.
    }
  }

  private bindEvents(): void {
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.onSubmit();
    });

    if (!this.select) return;

    this.select.addEventListener('change', () => {
      const isOther = this.select?.value === 'other';

      this.otherBusinessFieldWrapper?.classList.toggle('d-none', !isOther);
      if (this.otherInput) {
        this.otherInput.required = isOther;
      }

      if (!isOther && this.otherInput && this.select) {
        this.otherInput.value = '';
        this.select.name = 'business_area';
        this.otherInput.name = 'other_business';
      } else if (this.otherInput && this.select) {
        this.otherInput.name = 'business_area';
        this.select.name = 'other_business';
      }
    });
  }

  private setSubmittingState(isSubmitting: boolean): void {
    this.isSubmitting = isSubmitting;

    if (this.submitButton) {
      this.submitButton.disabled = isSubmitting;
      this.submitButton.innerHTML = isSubmitting ? 'Отправка...' : this.submitButtonText;
    }
  }

  private hideOtherBusinessField(): void {
    this.otherBusinessFieldWrapper?.classList.add('d-none');
    if (this.otherInput) {
      this.otherInput.value = '';
      this.otherInput.name = 'other_business';
    }
    if (this.select) {
      this.select.name = 'business_area';
    }
  }

  private getFieldValue(name: string): string {
    return this.form?.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      `[name="${name}"]`,
    )?.value.trim() ?? '';
  }

  private resolveBusinessAreaValue(): string {
    const businessArea = this.getFieldValue('business_area');
    if (businessArea === 'other') {
      return this.getFieldValue('other_business');
    }

    return businessArea;
  }

  private async onSubmit(): Promise<void> {
    if (!this.form || this.isSubmitting) {
      return;
    }

    const isValid = await beforeSubmit(this.form);
    if (!isValid) {
      return;
    }

    const documentsUpload = getResidencyBecomeDocumentsUpload();
    this.setSubmittingState(true);
    documentsUpload?.setSubmittingState(true);

    try {
      const readyFiles = documentsUpload?.getReadyFiles() ?? [];
      const privacyChecked = this.form.querySelector<HTMLInputElement>('#privacy')?.checked ?? false;

      const submitResult = await submitFormWithCaptcha({
        endpoint: RESIDENCE_BECOME_ENDPOINT,
        buildFormData: () => {
          const body = new FormData();

          body.append('full_name', this.getFieldValue('full_name'));
          body.append('business_area', this.resolveBusinessAreaValue());
          body.append('cluster', this.getFieldValue('cluster'));
          body.append('last_name', this.getFieldValue('last_name'));
          body.append('first_name', this.getFieldValue('first_name'));
          body.append('middle_name', this.getFieldValue('middle_name'));
          body.append('email', this.getFieldValue('email'));
          body.append('phone', this.getFieldValue('phone'));
          body.append('project_description', this.getFieldValue('project_description'));

          if (privacyChecked && this.privacyOptionId) {
            body.append('privacy', this.privacyOptionId);
          }

          readyFiles.forEach((file) => {
            body.append('documents[]', file);
          });

          return body;
        },
      });

      if (!submitResult.ok || !submitResult.data) {
        if (submitResult.errorCode === 'captcha-not-loaded') {
          showToast(getRecaptchaErrorMessage('captcha-not-loaded'), 'failed');
          return;
        }

        if (submitResult.errorCode === 'captcha-failed') {
          showToast(getRecaptchaErrorMessage('captcha-failed'), 'failed');
          return;
        }

        showToast(submitResult.message ?? this.errorMessageHtml, 'failed');
        return;
      }

      showToast(submitResult.data.msg ?? this.successMessageHtml, 'successed');
      trackYaGoal('submitApplication');
      this.form.reset();
      documentsUpload?.reset();
      this.hideOtherBusinessField();
    } catch {
      showToast(this.errorMessageHtml, 'failed');
    } finally {
      this.setSubmittingState(false);
      documentsUpload?.resetSubmittingState();
    }
  }
}

registerPageInit('residency-become', (root?: ParentNode) => {
  const page = new ResidencyPage();
  page.init(root);
});
